import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { applicationsAPI } from '../api';
import {
  Buildings as Building2,
  Envelope as Mail,
  Phone,
  User,
  Lock,
  Check,
  CheckCircle as CheckCircle2,
  WarningCircle as AlertCircle,
  CaretRight as ChevronRight,
  CaretLeft as ChevronLeft,
  Storefront as Store,
  ForkKnife as Utensils,
  Briefcase,
  Eye,
  EyeSlash as EyeOff,
  SpinnerGap,
  EnvelopeSimple,
  MagnifyingGlass,
  MapPin
} from '@phosphor-icons/react';
import { COUNTRIES } from '../data/countries';

const STORAGE_KEY = 'smartpos_signup_draft';

const businessTypes = [
  {
    value: 'RETAIL',
    label: 'Retail',
    icon: Store,
    description: 'Shops, electronics, groceries, pharmacy, clothing & general merchandise',
    subtypes: [
      { value: 'grocery', label: 'Grocery Store' },
      { value: 'electronics', label: 'Electronics Store' },
      { value: 'clothing', label: 'Clothing & Fashion' },
      { value: 'pharmacy', label: 'Pharmacy' },
      { value: 'supermarket', label: 'Supermarket' },
      { value: 'convenience', label: 'Convenience Store' },
      { value: 'hardware', label: 'Hardware Store' },
      { value: 'cosmetics', label: 'Cosmetics & Beauty' },
      { value: 'general', label: 'General Merchandise' },
      { value: 'other_retail', label: 'Other Retail' }
    ]
  },
  {
    value: 'FOOD_AND_BEVERAGE',
    label: 'Food & Beverage',
    icon: Utensils,
    description: 'Restaurants, cafes, bars, quick service & food trucks',
    subtypes: [
      { value: 'restaurant', label: 'Restaurant' },
      { value: 'cafe', label: 'Cafe / Coffee Shop' },
      { value: 'fast_food', label: 'Fast Food / QSR' },
      { value: 'bar', label: 'Bar / Lounge' },
      { value: 'bakery', label: 'Bakery' },
      { value: 'food_truck', label: 'Food Truck' },
      { value: 'catering', label: 'Catering Service' },
      { value: 'juice_bar', label: 'Juice Bar / Smoothies' },
      { value: 'other_food', label: 'Other Food & Beverage' }
    ]
  },
  {
    value: 'SERVICES',
    label: 'Services',
    icon: Briefcase,
    description: 'Salons, spas, auto repair, laundry & professional services',
    subtypes: [
      { value: 'salon', label: 'Salon / Barbershop' },
      { value: 'spa', label: 'Spa & Wellness' },
      { value: 'auto_repair', label: 'Auto Repair / Garage' },
      { value: 'laundry', label: 'Laundry / Dry Cleaning' },
      { value: 'cleaning', label: 'Cleaning Services' },
      { value: 'consulting', label: 'Consulting / Professional' },
      { value: 'repair', label: 'Repair Services' },
      { value: 'tutoring', label: 'Tutoring / Education' },
      { value: 'fitness', label: 'Fitness / Gym' },
      { value: 'other_services', label: 'Other Services' }
    ]
  }
];

const stepLabels = ['Account', 'Business', 'Location', 'Verify', 'Done'];

const Signup = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // OTP state
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [otpSending, setOtpSending] = useState(false);
  const [otpToken, setOtpToken] = useState(null);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpResendTimer, setOtpResendTimer] = useState(0);
  const otpInputRefs = useRef([]);

  // Business name check state
  const [businessNameAvailable, setBusinessNameAvailable] = useState(null);
  const [businessNameChecking, setBusinessNameChecking] = useState(false);
  const businessNameTimeout = useRef(null);

  const [countrySearch, setCountrySearch] = useState('');

  const [formData, setFormData] = useState({
    ownerFullName: '',
    ownerEmail: '',
    ownerPhone: '',
    username: '',
    password: '',
    businessName: '',
    businessType: '',
    businessSubtype: '',
    country: '',
    timezone: '',
    currency: '',
    currencySymbol: ''
  });

  const [errors, setErrors] = useState({});

  // Load saved form data from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Don't restore password or OTP-related state
        const { password, ...safeData } = parsed;
        setFormData(prev => ({ ...prev, ...safeData }));
      }
    } catch (e) {
      // Ignore parse errors
    }
  }, []);

  // Save form data to localStorage on change (exclude password)
  useEffect(() => {
    const { password, ...safeData } = formData;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(safeData));
  }, [formData]);

  // OTP resend countdown timer
  useEffect(() => {
    if (otpResendTimer <= 0) return;
    const interval = setInterval(() => {
      setOtpResendTimer(prev => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [otpResendTimer]);

  // Phone validation regex
  const phoneRegex = /^[\d\s\-+()]+$/;

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'ownerFullName':
        if (!value.trim()) error = 'Full name is required';
        else if (value.trim().length < 2) error = 'Must be at least 2 characters';
        else if (value.trim().length > 100) error = 'Must be 100 characters or less';
        break;
      case 'ownerEmail':
        if (!value.trim()) error = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'Invalid email format';
        else if (value.length > 100) error = 'Must be 100 characters or less';
        break;
      case 'ownerPhone':
        if (value.trim()) {
          if (value.length < 10) error = 'Must be at least 10 characters';
          else if (value.length > 20) error = 'Must be 20 characters or less';
          else if (!phoneRegex.test(value)) error = 'Only digits, spaces, dashes, +, () allowed';
        }
        break;
      case 'username':
        if (!value.trim()) error = 'Username is required';
        else if (value.length < 3) error = 'Must be at least 3 characters';
        else if (value.length > 20) error = 'Must be 20 characters or less';
        else if (!/^[a-zA-Z0-9_.]+$/.test(value)) error = 'Only letters, numbers, underscores, dots';
        break;
      case 'password':
        if (!value) error = 'Password is required';
        else if (value.length < 6) error = 'Must be at least 6 characters';
        break;
      case 'businessName':
        if (!value.trim()) error = 'Business name is required';
        else if (value.trim().length < 2) error = 'Must be at least 2 characters';
        else if (value.trim().length > 100) error = 'Must be 100 characters or less';
        break;
      default:
        break;
    }
    return error;
  };

  const isFieldValid = (name) => {
    const value = formData[name];
    if (name === 'businessName') {
      return value && !validateField(name, value) && businessNameAvailable === true;
    }
    return value && !validateField(name, value);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    const fieldError = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: fieldError }));
  };

  // Business name change with async uniqueness check
  const handleBusinessNameChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, businessName: value }));
    const fieldError = validateField('businessName', value);
    setErrors(prev => ({ ...prev, businessName: fieldError }));
    setBusinessNameAvailable(null);

    if (businessNameTimeout.current) clearTimeout(businessNameTimeout.current);

    if (!fieldError && value.trim().length >= 2) {
      setBusinessNameChecking(true);
      businessNameTimeout.current = setTimeout(async () => {
        try {
          const res = await applicationsAPI.checkBusinessName(value.trim());
          setBusinessNameAvailable(res.data.available);
          if (!res.data.available) {
            setErrors(prev => ({ ...prev, businessName: 'This business name is already taken' }));
          }
        } catch {
          // Silently fail – server-side will catch it on submit
        } finally {
          setBusinessNameChecking(false);
        }
      }, 500);
    } else {
      setBusinessNameChecking(false);
    }
  };

  const handleBusinessTypeSelect = (type) => {
    setFormData(prev => ({ ...prev, businessType: type, businessSubtype: '' }));
    setErrors(prev => ({ ...prev, businessType: '', businessSubtype: '' }));
  };

  const handleSubtypeSelect = (subtype) => {
    setFormData(prev => ({ ...prev, businessSubtype: subtype }));
    setErrors(prev => ({ ...prev, businessSubtype: '' }));
  };

  const handleCountrySelect = (country) => {
    setFormData(prev => ({
      ...prev,
      country: country.code,
      timezone: country.timezone,
      currency: country.currency,
      currencySymbol: country.currencySymbol
    }));
    setErrors(prev => ({ ...prev, country: '' }));
  };

  const validateStep3 = () => {
    if (!formData.country) {
      setErrors(prev => ({ ...prev, country: 'Please select your country' }));
      return false;
    }
    return true;
  };

  const validateStep1 = () => {
    const newErrors = {};
    ['ownerFullName', 'ownerEmail', 'username', 'password'].forEach(field => {
      const err = validateField(field, formData[field]);
      if (err) newErrors[field] = err;
    });
    const phoneErr = validateField('ownerPhone', formData.ownerPhone);
    if (phoneErr) newErrors.ownerPhone = phoneErr;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    const nameErr = validateField('businessName', formData.businessName);
    if (nameErr) newErrors.businessName = nameErr;
    if (businessNameAvailable === false) newErrors.businessName = 'This business name is already taken';
    if (!formData.businessType) newErrors.businessType = 'Please select a business type';
    if (formData.businessType && !formData.businessSubtype) newErrors.businessSubtype = 'Please select a category';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    setError('');
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    } else if (step === 3 && validateStep3()) {
      setStep(4);
      if (!otpVerified) {
        sendOTP();
      }
    }
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
    setError('');
  };

  // OTP Functions
  const sendOTP = async () => {
    setOtpSending(true);
    setError('');
    try {
      await applicationsAPI.sendOTP(formData.ownerEmail);
      setOtpResendTimer(60);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to send verification code';
      setError(msg);
    } finally {
      setOtpSending(false);
    }
  };

  const handleOtpChange = useCallback((index, value) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);
    setOtpCode(prev => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });

    // Auto-advance to next input
    if (digit && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  }, []);

  const handleOtpKeyDown = useCallback((index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  }, [otpCode]);

  const handleOtpPaste = useCallback((e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newCode = [...otpCode];
      for (let i = 0; i < 6; i++) {
        newCode[i] = pasted[i] || '';
      }
      setOtpCode(newCode);
      // Focus the last filled input or the next empty one
      const focusIndex = Math.min(pasted.length, 5);
      otpInputRefs.current[focusIndex]?.focus();
    }
  }, [otpCode]);

  // Auto-verify when all 6 digits entered
  useEffect(() => {
    const code = otpCode.join('');
    if (code.length === 6 && !otpVerified && !loading) {
      verifyOTP(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpCode]);

  const verifyOTP = async (code) => {
    setLoading(true);
    setError('');
    try {
      const res = await applicationsAPI.verifyOTP(formData.ownerEmail, code);
      setOtpToken(res.data.otpToken);
      setOtpVerified(true);
      // Auto-submit after short delay so user sees the success state
      setTimeout(() => handleSubmit(res.data.otpToken), 600);
    } catch (err) {
      const msg = err.response?.data?.error || 'Verification failed';
      setError(msg);
      setOtpCode(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (token) => {
    setLoading(true);
    setError('');
    try {
      const submitData = {
        ownerFullName: formData.ownerFullName,
        ownerEmail: formData.ownerEmail,
        username: formData.username,
        password: formData.password,
        businessName: formData.businessName,
        businessType: formData.businessType,
        businessSubtype: formData.businessSubtype,
        country: formData.country,
        timezone: formData.timezone,
        currency: formData.currency,
        currencySymbol: formData.currencySymbol,
        otpToken: token || otpToken
      };

      if (formData.ownerPhone?.trim()) {
        submitData.ownerPhone = formData.ownerPhone;
      }

      await applicationsAPI.signup(submitData);

      // Clear saved form data
      localStorage.removeItem(STORAGE_KEY);

      setStep(4);
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.errors && Array.isArray(errorData.errors)) {
        setError(errorData.errors.map(e => e.msg).join('. '));
      } else {
        setError(errorData?.error || 'Failed to submit application');
      }
    } finally {
      setLoading(false);
    }
  };

  const getSelectedBusinessType = () => businessTypes.find(t => t.value === formData.businessType);

  // Helper to render validation icon for a field
  const fieldIcon = (name) => {
    if (!formData[name]) return null;
    if (isFieldValid(name)) return <CheckCircle2 weight="bold" className="w-5 h-5 text-emerald-500" />;
    if (errors[name]) return <AlertCircle weight="bold" className="w-5 h-5 text-red-500" />;
    return null;
  };

  // Helper for input class based on validation state
  const inputClass = (name) =>
    `w-full pl-10 pr-10 py-3 border rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors ${
      errors[name] ? 'border-red-500 bg-red-50' : isFieldValid(name) ? 'border-emerald-500 bg-emerald-50' : 'border-primaryB-200'
    }`;

  const renderStep1 = () => (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-primaryB-900 mb-1">Create Your Account</h3>
        <p className="text-sm text-primaryB-500">Your personal details and login credentials</p>
      </div>

      {/* Full Name */}
      <div>
        <label className="block text-sm font-medium text-primaryB-600 mb-1.5">
          Full Name <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <User weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primaryB-400" />
          <input type="text" name="ownerFullName" value={formData.ownerFullName} onChange={handleChange} placeholder="John Doe" autoComplete="name" className={inputClass('ownerFullName')} />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">{fieldIcon('ownerFullName')}</div>
        </div>
        <p className={`mt-1 text-xs ${isFieldValid('ownerFullName') ? 'text-emerald-600' : 'text-primaryB-500'}`}>
          {isFieldValid('ownerFullName') ? '✓ Valid' : '2-100 characters'}
        </p>
        {errors.ownerFullName && <p className="mt-1 text-sm text-red-500">{errors.ownerFullName}</p>}
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-primaryB-600 mb-1.5">
          Email <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Mail weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primaryB-400" />
          <input type="email" name="ownerEmail" value={formData.ownerEmail} onChange={handleChange} placeholder="you@example.com" autoComplete="email" inputMode="email" className={inputClass('ownerEmail')} />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">{fieldIcon('ownerEmail')}</div>
        </div>
        <p className={`mt-1 text-xs ${isFieldValid('ownerEmail') ? 'text-emerald-600' : 'text-primaryB-500'}`}>
          {isFieldValid('ownerEmail') ? '✓ Valid' : "We'll verify this email"}
        </p>
        {errors.ownerEmail && <p className="mt-1 text-sm text-red-500">{errors.ownerEmail}</p>}
      </div>

      {/* Phone */}
      <div>
        <label className="block text-sm font-medium text-primaryB-600 mb-1.5">
          Phone <span className="text-primaryB-400 text-xs font-normal">(optional)</span>
        </label>
        <div className="relative">
          <Phone weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primaryB-400" />
          <input type="tel" name="ownerPhone" value={formData.ownerPhone} onChange={handleChange} placeholder="+233 XX XXX XXXX" autoComplete="tel" inputMode="tel" className={inputClass('ownerPhone')} />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">{fieldIcon('ownerPhone')}</div>
        </div>
        <p className={`mt-1 text-xs ${isFieldValid('ownerPhone') ? 'text-emerald-600' : 'text-primaryB-500'}`}>
          {isFieldValid('ownerPhone') ? '✓ Valid' : '10-20 characters'}
        </p>
        {errors.ownerPhone && <p className="mt-1 text-sm text-red-500">{errors.ownerPhone}</p>}
      </div>

      {/* Username */}
      <div>
        <label className="block text-sm font-medium text-primaryB-600 mb-1.5">
          Username <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <User weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primaryB-400" />
          <input type="text" name="username" value={formData.username} onChange={handleChange} placeholder="john.doe" autoComplete="username" className={inputClass('username')} />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">{fieldIcon('username')}</div>
        </div>
        <p className={`mt-1 text-xs ${isFieldValid('username') ? 'text-emerald-600' : 'text-primaryB-500'}`}>
          {isFieldValid('username') ? '✓ Valid' : '3-20 characters, letters/numbers/underscores/dots'}
        </p>
        {errors.username && <p className="mt-1 text-sm text-red-500">{errors.username}</p>}
      </div>

      {/* Password */}
      <div>
        <label className="block text-sm font-medium text-primaryB-600 mb-1.5">
          Password <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Lock weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primaryB-400" />
          <input
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="At least 6 characters"
            autoComplete="new-password"
            className={`w-full pl-10 pr-12 py-3 border rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors ${
              errors.password ? 'border-red-500 bg-red-50' : isFieldValid('password') ? 'border-emerald-500 bg-emerald-50' : 'border-primaryB-200'
            }`}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-primaryB-400 hover:text-primaryB-500 transition-colors"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff weight="bold" className="w-5 h-5" /> : <Eye weight="bold" className="w-5 h-5" />}
          </button>
        </div>
        <p className={`mt-1 text-xs ${formData.password.length >= 6 ? 'text-emerald-600' : 'text-primaryB-500'}`}>
          {formData.password.length >= 6 ? '✓ Password is valid' : `At least 6 characters (${formData.password.length}/6)`}
        </p>
        {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-primaryB-900 mb-1">Business Information</h3>
        <p className="text-sm text-primaryB-500">Tell us about your business</p>
      </div>

      {/* Business Name with async check */}
      <div>
        <label className="block text-sm font-medium text-primaryB-600 mb-1.5">
          Business Name <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Building2 weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primaryB-400" />
          <input
            type="text"
            name="businessName"
            value={formData.businessName}
            onChange={handleBusinessNameChange}
            placeholder="Your Business Name"
            autoComplete="organization"
            className={`w-full pl-10 pr-10 py-3 border rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors ${
              errors.businessName ? 'border-red-500 bg-red-50' : isFieldValid('businessName') ? 'border-emerald-500 bg-emerald-50' : 'border-primaryB-200'
            }`}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {businessNameChecking ? (
              <SpinnerGap weight="bold" className="w-5 h-5 text-primaryB-400 animate-spin" />
            ) : formData.businessName ? (
              isFieldValid('businessName') ? (
                <CheckCircle2 weight="bold" className="w-5 h-5 text-emerald-500" />
              ) : errors.businessName ? (
                <AlertCircle weight="bold" className="w-5 h-5 text-red-500" />
              ) : null
            ) : null}
          </div>
        </div>
        <p className={`mt-1 text-xs ${isFieldValid('businessName') ? 'text-emerald-600' : businessNameAvailable === false ? 'text-red-500' : 'text-primaryB-500'}`}>
          {isFieldValid('businessName') ? '✓ Name is available' : businessNameAvailable === false ? 'This name is taken' : '2-100 characters'}
        </p>
        {errors.businessName && <p className="mt-1 text-sm text-red-500">{errors.businessName}</p>}
      </div>

      {/* Business Type */}
      <div>
        <label className="block text-sm font-medium text-primaryB-600 mb-3">
          What type of business do you run? <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {businessTypes.map((type) => {
            const Icon = type.icon;
            const isSelected = formData.businessType === type.value;
            return (
              <button
                key={type.value}
                type="button"
                onClick={() => handleBusinessTypeSelect(type.value)}
                className={`p-4 rounded-2xl border-2 text-left transition-all ${
                  isSelected
                    ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500/20'
                    : 'border-primaryB-200 hover:border-primaryB-300 hover:bg-primaryB-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'bg-emerald-600 text-white' : 'bg-primaryB-100 text-primaryB-500'
                  }`}>
                    <Icon weight="bold" className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold ${isSelected ? 'text-emerald-700' : 'text-primaryB-900'}`}>
                      {type.label}
                    </p>
                    <p className={`text-xs mt-0.5 ${isSelected ? 'text-emerald-600' : 'text-primaryB-500'}`}>
                      {type.description}
                    </p>
                  </div>
                  {isSelected && (
                    <div className="w-5 h-5 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
                      <Check weight="bold" className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        {errors.businessType && <p className="mt-2 text-sm text-red-500">{errors.businessType}</p>}
      </div>

      {/* Business Subtype */}
      {formData.businessType && (
        <div>
          <label className="block text-sm font-medium text-primaryB-600 mb-3">
            What specific type of {getSelectedBusinessType()?.label.toLowerCase()} is this? <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {getSelectedBusinessType()?.subtypes.map((subtype) => {
              const isSelected = formData.businessSubtype === subtype.value;
              return (
                <button
                  key={subtype.value}
                  type="button"
                  onClick={() => handleSubtypeSelect(subtype.value)}
                  className={`px-3 py-2.5 rounded-xl border-2 text-sm font-medium transition-all ${
                    isSelected
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-primaryB-200 text-primaryB-600 hover:border-primaryB-300 hover:bg-primaryB-50'
                  }`}
                >
                  {subtype.label}
                </button>
              );
            })}
          </div>
          {errors.businessSubtype && <p className="mt-2 text-sm text-red-500">{errors.businessSubtype}</p>}
        </div>
      )}
    </div>
  );

  const renderStep3 = () => {
    const filteredCountries = COUNTRIES.filter(c =>
      c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
      c.code.toLowerCase().includes(countrySearch.toLowerCase()) ||
      c.currency.toLowerCase().includes(countrySearch.toLowerCase())
    );
    const selectedCountry = COUNTRIES.find(c => c.code === formData.country);

    return (
      <div className="space-y-5">
        <div>
          <h3 className="text-lg font-semibold text-primaryB-900 mb-1">Where is your business located?</h3>
          <p className="text-sm text-primaryB-500">Sets your local currency and timezone automatically</p>
        </div>

        {/* Selected country summary */}
        {selectedCountry && (
          <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center gap-3">
            <span className="text-2xl">{selectedCountry.flag}</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-emerald-800">{selectedCountry.name}</p>
              <p className="text-xs text-emerald-600">{selectedCountry.currencySymbol} {selectedCountry.currency} · {selectedCountry.timezone}</p>
            </div>
            <CheckCircle2 weight="bold" className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <MagnifyingGlass weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primaryB-400" />
          <input
            type="text"
            placeholder="Search country..."
            value={countrySearch}
            onChange={e => setCountrySearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-primaryB-200 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors"
          />
        </div>

        {/* Country list */}
        <div className="max-h-64 overflow-y-auto rounded-2xl border border-primaryB-200 divide-y divide-primaryB-100">
          {filteredCountries.map(country => (
            <button
              key={country.code}
              type="button"
              onClick={() => handleCountrySelect(country)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                formData.country === country.code
                  ? 'bg-emerald-50'
                  : 'hover:bg-primaryB-50'
              }`}
            >
              <span className="text-xl flex-shrink-0">{country.flag}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-primaryB-900 truncate">{country.name}</p>
                <p className="text-xs text-primaryB-500">{country.currencySymbol} {country.currency}</p>
              </div>
              {formData.country === country.code && (
                <Check weight="bold" className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              )}
            </button>
          ))}
          {filteredCountries.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-primaryB-500">No countries found</div>
          )}
        </div>

        {errors.country && <p className="text-sm text-red-500">{errors.country}</p>}
      </div>
    );
  };

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <EnvelopeSimple weight="bold" className="w-7 h-7 text-emerald-600" />
        </div>
        <h3 className="text-lg font-semibold text-primaryB-900 mb-1">Verify Your Email</h3>
        <p className="text-sm text-primaryB-500">
          We sent a 6-digit code to <span className="font-medium text-primaryB-900">{formData.ownerEmail}</span>
        </p>
      </div>

      {/* OTP Input */}
      <div className="flex justify-center gap-2">
        {otpCode.map((digit, i) => (
          <input
            key={i}
            ref={el => otpInputRefs.current[i] = el}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleOtpChange(i, e.target.value)}
            onKeyDown={(e) => handleOtpKeyDown(i, e)}
            onPaste={i === 0 ? handleOtpPaste : undefined}
            className={`w-12 h-14 text-center text-xl font-bold border-2 rounded-2xl outline-none transition-colors ${
              otpVerified
                ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                : digit
                  ? 'border-[#2D2A26] bg-primaryB-50 text-primaryB-900'
                  : 'border-primaryB-200 bg-primaryB-50 text-primaryB-900'
            } focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20`}
            disabled={otpVerified || loading}
          />
        ))}
      </div>

      {otpVerified && (
        <div className="flex items-center justify-center gap-2 text-emerald-600">
          <CheckCircle2 weight="bold" className="w-5 h-5" />
          <span className="text-sm font-medium">Email verified! Submitting your application...</span>
        </div>
      )}

      {loading && !otpVerified && (
        <div className="flex items-center justify-center gap-2 text-primaryB-500">
          <SpinnerGap weight="bold" className="w-5 h-5 animate-spin" />
          <span className="text-sm">Verifying...</span>
        </div>
      )}

      {/* Resend */}
      {!otpVerified && (
        <div className="text-center">
          <p className="text-sm text-primaryB-500 mb-2">Didn't receive the code?</p>
          {otpResendTimer > 0 ? (
            <p className="text-sm text-primaryB-400">Resend in {otpResendTimer}s</p>
          ) : (
            <button
              type="button"
              onClick={sendOTP}
              disabled={otpSending}
              className="text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors disabled:opacity-50"
            >
              {otpSending ? 'Sending...' : 'Resend Code'}
            </button>
          )}
        </div>
      )}
    </div>
  );

  const renderStep5 = () => (
    <div className="text-center space-y-6">
      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
        <Check weight="bold" className="w-8 h-8 text-emerald-600" />
      </div>
      <div>
        <h3 className="text-xl font-semibold text-primaryB-900 mb-2">You're all set!</h3>
        <p className="text-primaryB-500">
          We've sent your login details to <span className="font-medium text-primaryB-900">{formData.ownerEmail}</span>
        </p>
      </div>

      <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-4 text-left">
        <p className="text-sm font-semibold text-emerald-800 mb-2">Get started in 3 steps</p>
        <ol className="text-sm text-emerald-700 space-y-1.5 list-decimal list-inside">
          <li>Check your email for your login link</li>
          <li>Sign in and add your products</li>
          <li>Start selling – your free month begins now</li>
        </ol>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Link
          to="/find-business"
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-2xl hover:bg-emerald-700 transition-colors font-medium"
        >
          Find Your Business
        </Link>
        <Link
          to="/"
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 border border-primaryB-200 text-primaryB-600 rounded-2xl hover:bg-primaryB-50 transition-colors font-medium"
        >
          Back to Home
        </Link>
      </div>

      <p className="text-xs text-primaryB-400 text-center">
        Need help?{' '}
        <a href="https://wa.me/233559306229" target="_blank" rel="noopener noreferrer" className="text-emerald-600 font-semibold hover:underline">
          Chat with us on WhatsApp
        </a>
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-primaryB-50 flex items-center justify-center p-4">
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="w-full max-w-lg">
        <div className="bg-primaryB-50 rounded-2xl shadow-elevated border border-primaryB-200 overflow-hidden">
          {/* Header */}
          <div className="bg-primaryB-900 px-6 py-6 text-center relative">
            <button
              onClick={() => navigate(-1)}
              className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-1 text-white/80 hover:text-white transition-colors text-sm font-medium"
            >
              <ChevronLeft weight="bold" className="w-4 h-4" />
              Back
            </button>
            <Link to="/" className="inline-flex items-center justify-center gap-3 mb-1">
              <img src="/images/logo.png" alt="SmartPOS Africa" className="w-10 h-10 rounded-2xl bg-primaryB-50 p-1" />
              <h1 className="text-xl sm:text-2xl font-bold text-white">Smart POS</h1>
            </Link>
            <p className="text-white/60">Start Your 1 Month Free Trial</p>
          </div>

          {/* Step Indicator */}
          {step < 5 && (
            <div className="px-6 py-4 bg-primaryB-50 border-b border-primaryB-200">
              <div className="flex items-center justify-between">
                {stepLabels.slice(0, 4).map((label, idx) => {
                  const num = idx + 1;
                  return (
                    <React.Fragment key={num}>
                      <div className="flex flex-col items-center gap-1.5">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                            step > num
                              ? 'bg-emerald-600 text-white'
                              : step === num
                                ? 'bg-emerald-600 text-white'
                                : 'bg-[#E5E1DA] text-primaryB-400'
                          }`}
                        >
                          {step > num ? <Check weight="bold" className="w-4 h-4" /> : num}
                        </div>
                        <span className={`text-[10px] font-medium ${step >= num ? 'text-emerald-700' : 'text-primaryB-400'}`}>
                          {label}
                        </span>
                      </div>
                      {idx < 3 && (
                        <div
                          className={`flex-1 h-1 rounded-full mx-1 mb-5 transition-colors ${
                            step > num ? 'bg-emerald-600' : 'bg-[#E5E1DA]'
                          }`}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}

          {/* Form Content */}
          <div className="p-6">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={(e) => e.preventDefault()}>
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
              {step === 4 && renderStep4()}
              {step === 5 && renderStep5()}

              {step <= 3 && (
                <div className="flex gap-3 mt-8">
                  {step > 1 && (
                    <button
                      type="button"
                      onClick={handleBack}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 border border-primaryB-200 text-primaryB-600 rounded-2xl hover:bg-primaryB-50 transition-colors font-medium"
                    >
                      <ChevronLeft weight="bold" className="w-4 h-4" />
                      Back
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={businessNameChecking}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-2xl hover:bg-emerald-700 transition-colors font-medium disabled:opacity-50"
                  >
                    Next
                    <ChevronRight weight="bold" className="w-4 h-4" />
                  </button>
                </div>
              )}

              {step === 4 && !otpVerified && (
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-primaryB-200 text-primaryB-600 rounded-2xl hover:bg-primaryB-50 transition-colors font-medium"
                  >
                    <ChevronLeft weight="bold" className="w-4 h-4" />
                    Back
                  </button>
                </div>
              )}
            </form>

            {step < 5 && (
              <p className="text-center text-sm text-primaryB-500 mt-6">
                Already have an account?{' '}
                <Link to="/find-business" className="text-emerald-600 font-medium hover:text-emerald-700">
                  Sign in
                </Link>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
